import { useState, useContext, useRef, useCallback } from 'react';
import { MenuProfile } from '../header/MenuProfile';
import { MenuContainer } from '../header/MenuContainer';
import { MenuItem } from '../header/MenuItem';
import { Home, CreditCard, Star, Terminal, LogOut, Gift, Moon, Sun } from 'react-feather';
import { open } from '@/redux/app/actions/navigation';
import { colors } from '../ui-elements/Main/MainTheme';
import { useDispatch, useSelector } from 'react-redux';
import { AccountInfoContext } from '../account/account-info';
import { OrgInfoContext } from '../organization/org-info';
import { useNavigationActions } from '@/lib/navigation';
import { signOut } from '@/redux/app/actions/settings';
import { setThemePreference } from '@/redux/app/actions/theme';
import { Avatar } from './Avatar';
import { accountBillingPath, accountRewardsPath, organizationPath } from '@/helpers/app/routes';
import { UserInfoContext } from '../user/user-info';
import { PauseNotificationsMenuItem, PauseNotificationsSubmenu } from './PauseNotificationsMenu';
import { FlexAlignCenter, FlexColumn } from '../shared/Flex';
import styled from 'styled-components';
import { useClickOutside, useHover } from '@/helpers/app/hooks';
import { useViewport } from '@/lib/layout/dimensions';
import { ToolTip } from './ToolTip';

export const ProfileAndSettings = () => {
  const dispatch = useDispatch();
  const { navigate } = useNavigationActions();
  const { subdomain } = useContext(AccountInfoContext);
  const { hashId } = useContext(OrgInfoContext);
  const { canViewBilling, admin }  = useContext(UserInfoContext);
  const { canViewOrg } = useContext(OrgInfoContext);
  const themePreference = useSelector((state: any) => state.theme?.preference ?? 'light');
  const isDark = themePreference === 'dark';

  const ref = useRef();
  const hoverRef = useRef();
  const isMobile = useViewport((width) => width < 600, 500);
  const [show, setShow] = useState(false);
  const [hover, setHover] = useState(false);
  const [showPauseSubmenu, setShowPauseSubmenu] = useState(false);

  const handleClickOutside = useCallback((val: boolean) => {
    setShow(val);
    if (!val) setShowPauseSubmenu(false);
  }, []);
  useClickOutside(ref, handleClickOutside);
  useHover(hoverRef, setHover);

  const size = 18;

  const menuItems = [
    ...((canViewOrg || admin) ? [{
      title: 'Manage Organization',
      icon: <Home size={size}/>,
      callback: () => navigate({ path: organizationPath(hashId), params: {} })
    }] : []),
    ...(canViewBilling ? [{
      title: 'Billing & Subscription',
      icon: <CreditCard size={size}/>,
      callback: () => navigate({ path: accountBillingPath(subdomain), params: {} })
    }] : []),
    {
      title: 'Referrals & Rewards',
      icon: <Gift size={size}/>,
      callback: () => navigate({ path: accountRewardsPath(subdomain), params: {} })
    },
    { title: 'Whats New', icon: <Star size={size}/>, callback: () => dispatch(open({ path: `https://releases.avochato.com`, params: {} }))},
    { title: 'Developers', icon: <Terminal size={size}/>, callback: () => dispatch(open({ path: `${window.location.origin}/docs`, params: {} }))},
    ...(admin ? [{
      title: 'Toggle Stage',
      icon: <Star size={size}/>,
      callback: () => {
        if (window.ReactNativeWebView) {
          window.ReactNativeWebView.postMessage(JSON.stringify({version: "2", method: "switch-stage"}));
        }
      }}] : []),
    {
      title: isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode',
      icon: isDark ? <Sun size={size} /> : <Moon size={size} />,
      callback: () => dispatch(setThemePreference(isDark ? 'light' : 'dark')),
    },
    { title: 'Sign Out', icon: <LogOut size={size} color={colors.red}/>, textColor: colors.red, callback: () => dispatch(signOut())},
  ];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <FlexAlignCenter
        ref={hoverRef}
        onClick={() => {
          setShow(!show);
          if (show) setShowPauseSubmenu(false);
        }}
        style={{ cursor: 'pointer' }}
      >
        <Avatar />
      </FlexAlignCenter>
      {!isMobile && hover && !show && (
        <ToolTip>Profile & Settings</ToolTip>
      )}
      {show && (
        <MenuContainer>
          {showPauseSubmenu ? (
            <PauseNotificationsSubmenu onBack={() => setShowPauseSubmenu(false)} />
          ) : (
            <>
              <MenuProfile />
              <MenuItems>
                <PauseNotificationsMenuItem
                  onToggleSubmenu={setShowPauseSubmenu}
                />
              </MenuItems>
              <Divider />
              <MenuItems>
                {menuItems.map(({ textColor, icon, title, callback }) => (
                  <MenuItem
                    key={title}
                    icon={icon}
                    textColor={textColor}
                    title={title}
                    callback={callback}
                  />
                ))}
              </MenuItems>
            </>
          )}
        </MenuContainer>
      )}
    </div>
  )
}

const MenuItems = styled(FlexColumn)`
  padding: 10px;
`;

const Divider = styled.div`
  height: 1px;
  background: ${colors.greyer};
`;
